import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import { 
  connectFirestoreEmulator, 
  getFirestore,
  enableNetwork, 
  disableNetwork, 
  doc, 
  setDoc,
  getDoc,
  deleteDoc,
  collection, 
  query, 
  where
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  // Add these explicit settings for production
  databaseURL: `https://${process.env.VUE_APP_FIREBASE_PROJECT_ID}.firebaseio.com`,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID
};

console.log('Initializing Firebase with config:', {
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  databaseURL: firebaseConfig.databaseURL
});

// Initialize Firebase app
const app = initializeApp(firebaseConfig);

// Initialize App Check with ReCAPTCHA v3
let appCheck;

// Enable debug mode for App Check in development
if (process.env.NODE_ENV === 'development') {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  console.log('App Check debug mode enabled for development');
}

// Get the ReCAPTCHA key from environment variables
const recaptchaKey = process.env.VUE_APP_RECAPTCHA_SITE_KEY;

// Log environment variables for debugging (without exposing sensitive values)
console.log('Environment variables check:', {
  hasReCaptchaKey: !!recaptchaKey,
  envKeys: Object.keys(process.env).filter(key => key.includes('RECAPTCHA') || key.includes('CAPTCHA')),
  nodeEnv: process.env.NODE_ENV
});

// Hardcoded key as fallback (same as in .env)
const fallbackKey = '6LesQtwqAAAAADtPGIsWKBtrN6_RzT5rxLkOCLDL';

// Use the environment variable if available, otherwise use the fallback
const siteKey = recaptchaKey || fallbackKey;

// Initialize Auth with fallback persistence strategy
const auth = getAuth(app);

// Set up auth persistence with better error handling
const setupAuthPersistence = async () => {
  try {
    console.log('Setting up auth persistence...');
    await setPersistence(auth, browserLocalPersistence);
    console.log('Auth persistence set to local');
  } catch (error) {
    console.warn('Falling back to session persistence:', error);
    try {
      await setPersistence(auth, browserSessionPersistence);
      console.log('Auth persistence set to session');
    } catch (fallbackError) {
      console.error('Error setting auth persistence:', fallbackError);
    }
  }
};

// Call the setup function
setupAuthPersistence();

// Initialize Firestore with standard settings - no regional endpoint
const db = getFirestore(app);

console.log('Firestore initialized with default settings');

const storage = getStorage(app);

// Connect to emulators only in development mode and when not in production domain
if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
  console.log('Using Firebase emulators');
  connectAuthEmulator(auth, 'http://localhost:9098', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8084);
  connectStorageEmulator(storage, 'localhost', 9198);
  
  // Initialize App Check only after connecting to emulators
  // For local development with emulators, we'll skip the actual App Check initialization
  console.log('Skipping App Check initialization for local development with emulators');
} else {
  console.log('Running in production mode with network retry logic');
  
  // Initialize App Check with ReCAPTCHA v3 provider for production
  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true
    });
    console.log('App Check initialized with ReCAPTCHA v3 provider');
  } catch (error) {
    console.error('Failed to initialize App Check:', error);
  }
  
  // Set up network retry logic for production
  let networkAttempts = 0;
  const maxNetworkRetries = 3;
  
  // Add connection monitoring for debugging
  let connectionMonitorInterval = null;
  
  // Monitor connection status periodically
  window.monitorFirestoreConnection = (intervalMs = 5000) => {
    if (connectionMonitorInterval) {
      clearInterval(connectionMonitorInterval);
    }
    
    console.log('Starting Firestore connection monitor...');
    connectionMonitorInterval = setInterval(async () => {
      try {
        // Test with simple operation that shouldn't require permissions
        const testRef = doc(db, '_connectionTest', 'monitor');
        await setDoc(testRef, { timestamp: new Date().toISOString() }, { merge: true });
        console.log('✅ Firestore connection OK at', new Date().toISOString());
      } catch (error) {
        console.error('❌ Firestore connection FAILED:', error.code, error.message);
        
        // Check for DNS or connectivity errors specifically
        if (error.code === 'unavailable' || error.message.includes('name not resolved')) {
          console.error('DNS or connectivity issue detected with endpoint:', db._settings?.host);
        }
      }
    }, intervalMs);
    
    return () => {
      if (connectionMonitorInterval) {
        clearInterval(connectionMonitorInterval);
        connectionMonitorInterval = null;
        console.log('Firestore connection monitor stopped');
      }
    };
  };
  
  // Add network retry function
  window.retryFirestoreConnection = async () => {
    if (networkAttempts < maxNetworkRetries) {
      networkAttempts++;
      console.log(`Retrying Firestore connection (attempt ${networkAttempts}/${maxNetworkRetries})...`);
      
      try {
        await disableNetwork(db);
        console.log('Network disabled for reset');
        await new Promise(resolve => setTimeout(resolve, 1000));
        await enableNetwork(db);
        console.log('Network re-enabled');
        return true;
      } catch (error) {
        console.error('Network retry error:', error);
        return false;
      }
    } else {
      console.error('Max network retries reached');
      return false;
    }
  };
}

// Add a listener for auth state changes to help debug
auth.onAuthStateChanged((user) => {
  if (user) {
    console.log('Auth state changed: User is signed in', {
      uid: user.uid,
      email: user.email,
      providerId: user.providerId
    });
    
    // Force token refresh on auth state change
    user.getIdToken(true)
      .then(token => {
        console.log('Token refreshed on auth state change:', token ? 'Token exists' : 'No token');
      })
      .catch(error => {
        console.error('Error refreshing token:', error);
      });
  } else {
    console.log('Auth state changed: User is signed out');
  }
});

// Add explicit token validation for debugging
const validateToken = async () => {
  const user = auth.currentUser;
  if (user) {
    try {
      // Force token refresh and log details
      await user.getIdToken(true);
      const tokenResult = await user.getIdTokenResult();
      console.log('Token validation:', {
        uid: user.uid,
        provider: tokenResult.signInProvider,
        expires: new Date(tokenResult.expirationTime).toISOString(),
        issued: new Date(tokenResult.issuedAtTime).toISOString(),
        claims: Object.keys(tokenResult.claims)
      });
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }
  console.log('Token validation: No user signed in');
  return false;
};

// Create a simple test document to verify direct Firestore access
const testDirectAccess = async () => {
  try {
    console.log('Starting direct Firestore test...');
    // Directly use the Firestore methods
    const testDocRef = doc(db, 'userPreferences', 'test-' + Date.now());
    await setDoc(testDocRef, {
      test: true,
      timestamp: new Date().toISOString(),
      diagnosticRun: true
    });
    console.log('✅ Direct Firestore write test SUCCEEDED');
    
    // Try to read it back
    const docSnap = await getDoc(testDocRef);
    console.log('Direct read test:', docSnap.exists() ? 'SUCCEEDED' : 'FAILED - Doc does not exist');
    
    // Try to delete it
    await deleteDoc(testDocRef);
    console.log('Direct delete test: Complete');
    
    return true;
  } catch (error) {
    console.error('❌ Direct Firestore test FAILED:', error);
    console.error('Error details:', error.message, error.code);
    return false;
  }
};

// Add a helper to test userPreferences access
const testUserPreferences = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.error('Cannot test user preferences - not logged in');
    return false;
  }
  
  try {
    console.log('Testing user preferences access for:', user.uid);
    
    // Direct test of userPreferences access
    const userPrefRef = doc(db, 'userPreferences', user.uid);
    
    // Try reading preferences
    const docSnap = await getDoc(userPrefRef);
    
    if (docSnap.exists()) {
      console.log('✅ User preferences read SUCCESS:', docSnap.data());
    } else {
      console.log('User preferences do not exist yet - creating test document');
      
      // Create a test preferences document
      await setDoc(userPrefRef, {
        ethical: { testValue: 5 },
        political: { testValue: 5 },
        weights: { testValue: 5 },
        testRun: true,
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ Test preferences created successfully');
    }
    
    return true;
  } catch (error) {
    console.error('❌ User preferences test FAILED:', error);
    console.error('Error details:', error.message, error.code);
    return false;
  }
};

// Define our Firebase helpers object with v9 SDK
const firebaseHelpers = {
  db: db,
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  deleteDoc,
  validateToken,
  testDirectAccess,
  testUserPreferences
};

console.log('Setting up Firebase helper functions on window object');

// Expose our Firebase helpers directly on window
try {
  window.firestoreDb = db;
  window.validateFirebaseToken = validateToken;
  window.testFirestore = testDirectAccess;
  window.testUserPreferences = testUserPreferences;
  window.firestore = firebaseHelpers;
  
  // Add a function to test the currently authenticated user's document
  window.testCurrentUserDocument = async () => {
    const user = auth.currentUser;
    if (!user) {
      console.error('Cannot test user document - not logged in');
      return false;
    }
    
    try {
      console.log('Testing direct access to user document:', user.uid);
      const userPrefRef = doc(db, 'userPreferences', user.uid);
      
      // Try write first to create if needed
      await setDoc(userPrefRef, {
        test: true,
        timestamp: new Date().toISOString()
      }, { merge: true });
      
      // Try reading it back
      const docSnap = await getDoc(userPrefRef);
      const success = docSnap.exists();
      console.log(success ? 
        '✅ Direct user document test PASSED' : 
        '❌ Direct user document test FAILED');
      
      return success;
    } catch (error) {
      console.error('❌ User document test error:', error);
      return false;
    }
  };
  
  // For easier debugging, add direct v9 reference methods
  window.executeFirestoreTest = () => {
    console.log('Executing Firestore test with v9 syntax');
    const testDocRef = doc(db, 'userPreferences', 'test-console');
    return setDoc(testDocRef, { 
      test: true, 
      timestamp: new Date().toISOString() 
    })
    .then(() => console.log('Test document created successfully'))
    .catch(err => console.error('Error creating test document:', err));
  };

  // Test a different collection to see if the issue is specific to userPreferences
  window.testDifferentCollection = () => {
    console.log('Testing access to a different collection');
    const testCollection = 'diagnosticTests';
    const testDocRef = doc(db, testCollection, 'test-' + Date.now());
    
    console.log(`Writing to collection: ${testCollection}`);
    return setDoc(testDocRef, { 
      test: true, 
      timestamp: new Date().toISOString(),
      note: 'Testing alternative collection access'
    })
    .then(() => {
      console.log(`Successfully wrote to ${testCollection}`);
      return getDoc(testDocRef);
    })
    .then(docSnap => {
      if (docSnap.exists()) {
        console.log(`Successfully read from ${testCollection}:`, docSnap.data());
        return deleteDoc(testDocRef);
      } else {
        console.error(`Document in ${testCollection} doesn't exist after writing`);
        return false;
      }
    })
    .then(() => {
      console.log(`Successfully deleted test document from ${testCollection}`);
      return true;
    })
    .catch(err => {
      console.error(`Error with ${testCollection} collection:`, err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      // Check for specific permission errors
      if (err.code === 'permission-denied') {
        console.error('This is a permissions issue. Current Firestore rules may be too restrictive.');
      }
      return false;
    });
  };
  
  // Add a function to check specific document existence and permissions
  window.checkDocumentPermissions = (collectionPath, docId) => {
    const docRef = doc(db, collectionPath, docId || 'test');
    console.log(`Checking permissions for ${collectionPath}/${docId || 'test'}`);
    
    // First try to read
    return getDoc(docRef)
      .then(docSnap => {
        console.log(`Read permission for ${collectionPath}: ALLOWED`);
        console.log('Document exists:', docSnap.exists());
        if (docSnap.exists()) {
          console.log('Document data:', docSnap.data());
        }
        
        // Try to write
        return setDoc(docRef, { 
          permissionTest: true,
          timestamp: new Date().toISOString()
        }, { merge: true });
      })
      .then(() => {
        console.log(`Write permission for ${collectionPath}: ALLOWED`);
        return true;
      })
      .catch(err => {
        console.error(`Permission check failed for ${collectionPath}:`, err);
        console.error('Error code:', err.code);
        console.error('Error message:', err.message);
        
        if (err.code === 'permission-denied') {
          if (err.message.includes('read')) {
            console.error('READ permission is denied');
          }
          if (err.message.includes('write') || err.message.includes('create') || err.message.includes('update')) {
            console.error('WRITE permission is denied');
          }
        }
        return false;
      });
  };
  
  // Add global utilities for diagnostics and testing
  window.testFirebaseAuth = () => {
    return auth.currentUser ? {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email,
      isAnonymous: auth.currentUser.isAnonymous
    } : null;
  };

  // Check if our window assignments worked
  console.log('Firebase helpers initialized on window:', {
    firestoreDb: !!window.firestoreDb,
    validateFirebaseToken: !!window.validateFirebaseToken,
    testFirestore: !!window.testFirestore,
    testUserPreferences: !!window.testUserPreferences,
    firestore: !!window.firestore,
    testDifferentCollection: !!window.testDifferentCollection,
    checkDocumentPermissions: !!window.checkDocumentPermissions,
    testCurrentUserDocument: !!window.testCurrentUserDocument
  });

  // Add region test function
  window.testRegionConnectivity = async () => {
    try {
      console.log('Testing Firestore connectivity...');
      // Test document in a test collection
      const testRef = doc(db, 'regionTest', 'connectivity-test');
      await setDoc(testRef, {
        timestamp: new Date().toISOString(),
        testType: 'region-connectivity'
      });
      console.log('✅ Region connectivity test passed - write successful');
      
      // Try to read it back
      const docSnap = await getDoc(testRef);
      console.log('Region read test:', docSnap.exists() ? '✅ PASSED' : '❌ FAILED - Doc not found');
      
      // Clean up
      await deleteDoc(testRef);
      return true;
    } catch (error) {
      console.error('❌ Region connectivity test failed:', error);
      return {
        error: error.message,
        code: error.code,
        success: false
      };
    }
  };

  // Test function for database existence - will report clearly on what's happening
  window.testDatabaseExists = async () => {
    console.log('Testing database existence with endpoint:', db._settings?.host);
    try {
      // Create a unique test document ID
      const testId = `test-${Date.now()}`;
      const testRef = doc(db, '_dbtest', testId);
      const testData = { timestamp: new Date().toISOString() };
      
      console.log(`Writing test document to _dbtest/${testId}...`);
      await setDoc(testRef, testData);
      
      console.log('Write succeeded, database exists and is writable');
      return {
        exists: true, 
        canWrite: true,
        endpoint: db._settings?.host,
        testId
      };
    } catch (error) {
      console.error('Database test failed:', error);
      
      // Diagnose the specific error
      let diagnosis = 'Unknown error';
      if (error.code === 'permission-denied') {
        diagnosis = 'Database exists but permissions are incorrect';
      } else if (error.code === 'unavailable' || error.message.includes('name not resolved')) {
        diagnosis = 'Database endpoint cannot be reached - DNS or connectivity issue';
      } else if (error.code === 'not-found') {
        diagnosis = 'Database or project may not exist';
      }
      
      return {
        exists: false,
        error: error.message,
        code: error.code,
        diagnosis,
        endpoint: db._settings?.host
      };
    }
  };
} catch (error) {
  console.error('Error setting up window helpers:', error);
}

// Export the Firebase instances and configurations
export { 
  app, 
  auth, 
  db, 
  storage, 
  validateToken, 
  testDirectAccess,
  appCheck
}; 